import { useState, useEffect, useCallback } from "react";
import { Post, PostToBackend } from "../../../types/features/Post";
import { postService } from "../../../services/postService";
import useToken from "../auth/useToken";

export default function usePosts() {
  const { token } = useToken();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchPosts = useCallback(async (page: number = 1, perPage: number = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response = await postService.getAll(page, perPage);
      setPosts(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des articles");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchPostById = useCallback(async (id: string): Promise<Post | null> => {
    setLoading(true);
    setError(null);
    try {
      const post = await postService.getById(id);
      if (!post) {
        setError("Article non trouvé");
      }
      return post;
    } catch (err) {
      setError("Erreur lors du chargement de l'article");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchPostBySlug = useCallback(async (slug: string): Promise<Post | null> => {
    setLoading(true);
    setError(null);
    try {
      const post = await postService.getBySlug(slug);
      if (!post) {
        setError("Article non trouvé");
      }
      return post;
    } catch (err) {
      setError("Erreur lors du chargement de l'article");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔒 PROTÉGÉ - Token requis
  const createPost = useCallback(async (data: PostToBackend): Promise<Post | null> => {
          console.log(data)

    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const newPost = await postService.create(token, data);
      setPosts((prev) => [newPost, ...prev]);
      return newPost;
    } catch (err: any) {
      setError("Erreur lors de la création de l'article");
      console.error(err.response);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const updatePost = useCallback(async (id: string, data: Partial<PostToBackend>): Promise<Post | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const updatedPost = await postService.update(token, id, data);
      setPosts((prev) => prev.map((p) => (p.id === id ? updatedPost : p)));
      return updatedPost;
    } catch (err) {
      setError("Erreur lors de la mise à jour de l'article");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deletePost = useCallback(async (id: string): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await postService.delete(token, id);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression de l'article");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deleteManyPosts = useCallback(async (ids: string[]): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await postService.deleteMany(token, ids);
      setPosts((prev) => prev.filter((p) => !ids.includes(p.id)));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression multiple");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  return {
    posts,
    loading,
    error,
    pagination,
    fetchPosts,
    fetchPostById,
    fetchPostBySlug,
    createPost,
    updatePost,
    deletePost,
    deleteManyPosts,
  };
}